import { useState } from "react";
import { useHealth } from "@/context/HealthContext";
import { HCard } from "@/components/health-kit";
import { Button } from "@/components/ui/button";
import { PlaySquare, ArrowRight, ArrowLeft, X, Sparkles } from "lucide-react";

const STEPS = {
  de: [
    ["Das Problem", "Frühe Verschlechterungen in der Pflege bleiben oft unbemerkt, bis sie kritisch werden."],
    ["NeuNexus KI-Kern", "Verarbeitet Biomarker, Vitalwerte und Beobachtungen zu Trends und potenziellen Signalen."],
    ["Synthetischer Patient", "Ein realitätsnaher, aber vollständig synthetischer Fall — keine echten Patientendaten."],
    ["KI-Analyse", "Die KI erzeugt eine Zusammenfassung, Trendanalyse und eine Risikoeinstufung."],
    ["Potenzielles Signal", "Signale wie sinkende Hydratation oder Biomarker-Änderungen werden hervorgehoben."],
    ["Fachliche Prüfung", "Fachpersonal prüft, korrigiert oder lehnt jede KI-Ausgabe ab. Der Mensch entscheidet."],
    ["Audit-Trail", "Jede Entscheidung wird nachvollziehbar und verantwortlich protokolliert."],
    ["Digital Twin (Zukunftskonzept)", "Phase 3 — kein validiertes klinisches Produkt. Konzeptionelle Risiko-Trajektorien."],
    ["Pilot-Validierung", "Jede Metrik: NOCH ZU VALIDIEREN. Keine erfundenen Ergebnisse."],
    ["Was wir brauchen", "Partner, Validierung und Expertise, um den Weg gemeinsam zu gehen."],
  ],
  en: [
    ["The Problem", "Early deterioration in care settings often goes unnoticed until it becomes critical."],
    ["NeuNexus AI Core", "Turns biomarkers, vitals and observations into trends and potential signals."],
    ["Synthetic Patient", "A realistic but fully synthetic case — no real patient data."],
    ["AI Analysis", "The AI generates a summary, trend analysis and a risk stratification."],
    ["Potential Signal", "Signals such as decreasing hydration or biomarker changes are highlighted."],
    ["Human Review", "Professionals approve, correct or reject every AI output. The human decides."],
    ["Audit Trail", "Every decision is logged for traceability and accountability."],
    ["Digital Twin (Future Concept)", "Phase 3 — not a validated clinical product. Conceptual risk trajectories."],
    ["Pilot Validation", "Every metric: TO BE VALIDATED. No invented results."],
    ["What We Need", "Partners, validation and expertise to walk this pathway together."],
  ],
  pt: [
    ["O Problema", "A deterioração precoce no cuidado muitas vezes passa despercebida até se tornar crítica."],
    ["NeuNexus AI Core", "Transforma biomarcadores, vitais e observações em tendências e sinais potenciais."],
    ["Paciente Sintético", "Um caso realista, porém totalmente sintético — sem dados reais de pacientes."],
    ["Análise de IA", "A IA gera um resumo, análise de tendência e estratificação de risco."],
    ["Sinal Potencial", "Sinais como queda de hidratação ou mudanças em biomarcadores são destacados."],
    ["Revisão Humana", "Profissionais aprovam, corrigem ou rejeitam cada saída de IA. O humano decide."],
    ["Trilha de Auditoria", "Cada decisão é registrada para rastreabilidade e responsabilidade."],
    ["Digital Twin (Conceito Futuro)", "Fase 3 — não é um produto clínico validado. Trajetórias conceituais de risco."],
    ["Validação Piloto", "Cada métrica: A SER VALIDADA. Sem resultados inventados."],
    ["O Que Precisamos", "Parceiros, validação e expertise para trilhar este caminho juntos."],
  ],
};
const LOOKING = ["AI Expertise", "Digital Twin Expertise", "Healthcare Validation", "Pilot Partners", "Research & Technology Partners", "Regulatory Guidance", "Funding Guidance"];

export default function PresentationMode() {
  const { ht, lang } = useHealth();
  const steps = STEPS[lang] || STEPS.de;
  const [running, setRunning] = useState(false);
  const [i, setI] = useState(0);
  const last = i === steps.length - 1;

  if (!running) {
    return (
      <div className="ngx-fade-in max-w-2xl mx-auto text-center pt-10" data-testid="health-presentation">
        <div className="h-16 w-16 rounded-xl bg-teal-600/15 border border-teal-800 flex items-center justify-center mx-auto mb-6">
          <PlaySquare className="h-8 w-8 text-teal-400" />
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-50">{ht("presTitle")}</h1>
        <p className="text-slate-400 mt-2">{ht("presSub")}</p>
        <Button data-testid="start-presentation" onClick={() => { setRunning(true); setI(0); }} className="mt-8 h-12 px-6 bg-teal-600 hover:bg-teal-700 text-white">
          {ht("startPres")} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="ngx-fade-in max-w-3xl mx-auto" data-testid="presentation-runner">
      <div className="flex items-center justify-between mb-6">
        <span className="text-xs font-mono uppercase tracking-widest text-teal-400">{ht("step")} {String(i + 1).padStart(2, "0")} / {steps.length}</span>
        <button onClick={() => setRunning(false)} data-testid="presentation-exit" className="text-slate-500 hover:text-slate-200 flex items-center gap-1 text-sm"><X className="h-4 w-4" /> {ht("exit")}</button>
      </div>
      <div className="h-1 rounded-full bg-slate-800 overflow-hidden mb-8">
        <div className="h-full bg-teal-500 transition-all" style={{ width: `${((i + 1) / steps.length) * 100}%` }} />
      </div>

      <HCard className="p-8 min-h-[280px] flex flex-col justify-center">
        <div className="text-teal-400 font-mono text-sm mb-3">{String(i + 1).padStart(2, "0")}</div>
        <h2 className="font-display text-3xl font-bold text-slate-50">{steps[i][0]}</h2>
        <p className="text-slate-300 mt-4 text-lg leading-relaxed">{steps[i][1]}</p>

        {last && (
          <div className="mt-6">
            <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-widest mb-3"><Sparkles className="h-4 w-4" /> {ht("lookingFor")}</div>
            <div className="flex flex-wrap gap-2">
              {LOOKING.map((l) => <span key={l} className="text-xs px-2.5 py-1 rounded-sm bg-[#050C11] border border-teal-900 text-teal-200">{l}</span>)}
            </div>
            <p className="mt-5 text-sm text-slate-300 italic border-l-2 border-teal-600 pl-3">{ht("finalQuestion")}</p>
          </div>
        )}
      </HCard>

      <div className="flex items-center justify-between mt-6">
        <Button disabled={i === 0} onClick={() => setI((x) => x - 1)} data-testid="presentation-prev"
          className="h-11 bg-[#0A1620] border border-slate-700 text-slate-200 hover:border-teal-500 disabled:opacity-40"><ArrowLeft className="h-4 w-4 mr-2" /> {ht("prev")}</Button>
        <Button disabled={last} onClick={() => setI((x) => x + 1)} data-testid="presentation-next"
          className="h-11 bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40">{ht("next")} <ArrowRight className="h-4 w-4 ml-2" /></Button>
      </div>
    </div>
  );
}
